/**
 * The native side of the voice provider: registers the Twilio device for the
 * current property and wires every `CapacitorTwilioVoice` event (plus the iOS
 * foreground nudge) to the provider's state.
 *
 * A hook rather than provider body because it is one self-contained lifecycle
 * — one login, one listener set, one teardown — shaped like its
 * `useCallSimulators` sibling: it takes the provider's setters and refs.
 */

import { App } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import { CapacitorTwilioVoice } from "@capgo/capacitor-twilio-voice";
import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import type { useHistory } from "react-router-dom";
import { toast } from "sonner";
import { interfaceTranslator } from "@/i18n/translate";
import { APP_PUSH_STATE } from "@/lib/history-entry";
import { routeTemplates } from "@/lib/routes";
import { type ParsedCaller, parseCaller } from "./caller";
import { fetchTwilioVoiceToken } from "./client";
import { ensureMicGranted } from "./microphone";
import type {
  ActiveCall,
  PendingInvite,
  PlacingCall,
  RingingInvite,
} from "./voice-context";

/**
 * Longer than any call rings (the TwiML dials for 30 s). Some ends of a call
 * reach the app as no event it can match, an Android connect failure among
 * them, so an invite or a placed call older than this is no longer ringing.
 */
const RINGING_LIFETIME_MS = 60_000;

/** The one invite accepted in the app, dropping invites past any ring time. */
function acceptedInviteId(
  ringing: Map<string, RingingInvite>,
  now: number
): string | undefined {
  for (const [id, { receivedAt }] of ringing) {
    if (now - receivedAt > RINGING_LIFETIME_MS) {
      ringing.delete(id);
    }
  }
  const accepted = [...ringing].filter(([, invite]) => invite.accepted);
  return accepted.length === 1 ? accepted[0][0] : undefined;
}

/**
 * The invite a call that just connected was answered from. On iOS it shares
 * the call's id, always. Android tags an invite with an id of its own, never
 * the call's sid, so there it is the one invite the host accepted in the app,
 * or else the one invite ringing, and none when several ring or a call the
 * host placed may be the one connecting: a wrong caller is worse than none.
 */
function answeredInviteId(
  ringing: Map<string, RingingInvite>,
  callSid: string,
  { now, placing }: { now: number; placing: boolean }
): string | undefined {
  if (ringing.has(callSid)) {
    return callSid;
  }
  if (Capacitor.getPlatform() !== "android") {
    return;
  }
  const accepted = acceptedInviteId(ringing, now);
  if (accepted !== undefined || placing) {
    return accepted;
  }
  const [onlyId] = ringing.size === 1 ? ringing.keys() : [];
  return onlyId;
}

/** The placed call still pending, dropping one past any ring time. */
function pendingPlacedCall(
  ref: { current: PlacingCall | null },
  now: number
): PlacingCall | null {
  const placing = ref.current;
  if (placing && now - placing.placedAt > RINGING_LIFETIME_MS) {
    ref.current = null;
    return null;
  }
  return placing;
}

/**
 * The call on record once `callSid` rings, or `undefined` for no change. Only
 * an outgoing call rings. Android emits no `outgoingCallInitiated`, so its
 * call is first known here, by its sid, as the one `makeCall` placed, unless
 * a call the host answered meanwhile holds the slot; then the placed call
 * stays pending, now with its sid, until it connects.
 */
function ringingCall(
  callSid: string,
  {
    held,
    placingCallRef,
    now,
  }: {
    held: ActiveCall | null;
    now: number;
    placingCallRef: { current: PlacingCall | null };
  }
): ActiveCall | undefined {
  const placing = pendingPlacedCall(placingCallRef, now);
  if (held) {
    if (placing && held.callSid !== callSid) {
      placingCallRef.current = { ...placing, callSid };
    }
    return held.callSid === callSid
      ? { ...held, status: "ringing" }
      : undefined;
  }
  placingCallRef.current = null;
  return placing
    ? {
        callSid,
        direction: "outgoing",
        displayName: placing.displayName,
        muted: false,
        onHold: false,
        startedAt: null,
        status: "ringing",
        to: placing.to,
      }
    : undefined;
}

/**
 * A call connecting that is not on record yet: the call the host placed, or
 * a call they answered, newest taking the screen.
 */
function connectedCall(
  callSid: string,
  {
    caller,
    now,
    placedCall,
  }: { caller?: ParsedCaller; now: number; placedCall: PlacingCall | null }
): ActiveCall {
  const base = {
    callSid,
    muted: false,
    onHold: false,
    startedAt: now,
  } as const;
  return placedCall
    ? {
        ...base,
        direction: "outgoing",
        displayName: placedCall.displayName,
        status: "connected",
        to: placedCall.to,
      }
    : { ...base, caller, direction: "incoming", status: "connected" };
}

/**
 * What a call connecting off record is: the placed call that rang behind
 * another (taken out of pending), or one the host answered, with the invite
 * it names, if any, and that invite's caller taken out of the ringing record.
 */
function connectingCallParts(
  callSid: string,
  {
    now,
    placingCallRef,
    ringing,
  }: {
    now: number;
    placingCallRef: { current: PlacingCall | null };
    ringing: Map<string, RingingInvite>;
  }
): {
  caller?: ParsedCaller;
  inviteId?: string;
  placedCall: PlacingCall | null;
} {
  const placing = pendingPlacedCall(placingCallRef, now);
  if (placing?.callSid === callSid) {
    placingCallRef.current = null;
    return { placedCall: placing };
  }
  const inviteId = answeredInviteId(ringing, callSid, {
    now,
    placing: placing !== null,
  });
  if (inviteId === undefined) {
    return { placedCall: null };
  }
  const caller = ringing.get(inviteId)?.caller;
  ringing.delete(inviteId);
  return { caller, inviteId, placedCall: null };
}

export function useVoiceNativeListeners({
  activeCallRef,
  activePropertyId,
  dropCall,
  heldCallsRef,
  historyRef,
  pendingInviteRef,
  placingCallRef,
  ringingInvitesRef,
  recordCall,
  recordHeldCalls,
  recordHold,
  recordInvite,
  setRegistered,
  supported,
}: {
  activeCallRef: { current: ActiveCall | null };
  activePropertyId: string | null;
  dropCall: (callSid: string, options: { handBack: boolean }) => boolean;
  heldCallsRef: { current: ActiveCall[] };
  historyRef: { current: ReturnType<typeof useHistory> };
  pendingInviteRef: { current: PendingInvite | null };
  placingCallRef: { current: PlacingCall | null };
  ringingInvitesRef: { current: Map<string, RingingInvite> };
  recordCall: (next: ActiveCall | null) => void;
  recordHeldCalls: (next: ActiveCall[]) => void;
  recordHold: (callSid: string, onHold: boolean) => void;
  recordInvite: (next: PendingInvite | null) => void;
  setRegistered: Dispatch<SetStateAction<boolean>>;
  supported: boolean;
}): void {
  // biome-ignore lint/correctness/useExhaustiveDependencies: activePropertyId is part of the Twilio identity — re-running this effect when it changes re-registers the device under the new compound identity.
  useEffect(() => {
    if (!supported) {
      return;
    }

    const listeners: PluginListenerHandle[] = [];
    let _cancelled = false;

    /** Puts `next` on screen, keeping the live call it takes it from behind. */
    const takeScreen = (next: ActiveCall) => {
      const shown = activeCallRef.current;
      if (shown && shown.callSid !== next.callSid) {
        recordHeldCalls([...heldCallsRef.current, shown]);
      }
      recordCall(next);
    };

    /** Updates `callSid`'s call, on screen or held, saying whether it is one. */
    const updateCall = (
      callSid: string,
      update: (call: ActiveCall) => ActiveCall
    ): boolean => {
      const shown = activeCallRef.current;
      if (shown?.callSid === callSid) {
        recordCall(update(shown));
        return true;
      }
      const held = heldCallsRef.current;
      if (!held.some((call) => call.callSid === callSid)) {
        return false;
      }
      recordHeldCalls(
        held.map((call) => (call.callSid === callSid ? update(call) : call))
      );
      return true;
    };

    /** Drops an ended call from every record, saying whether one held it. */
    const forgetCall = (callSid: string | undefined): boolean => {
      if (callSid === undefined) {
        return false;
      }
      // Android ends a declined notification, or an accept refused the
      // microphone, with a disconnect carrying the invite's id.
      const wasRinging = ringingInvitesRef.current.delete(callSid);
      const wasPlaced = placingCallRef.current?.callSid === callSid;
      if (wasPlaced) {
        placingCallRef.current = null;
      }
      // A declined invite or a placed call ringing behind the live call
      // leaves it shown. Only iOS ends or mutes a call by its sid, so only
      // there does a held call take the screen back; Android's plugin acts on
      // its newest call alone.
      const wasOnRecord = dropCall(callSid, {
        handBack: Capacitor.getPlatform() === "ios",
      });
      return wasRinging || wasPlaced || wasOnRecord;
    };

    /**
     * An Android call the app holds no record of ending: with no sid yet, a
     * placement before it rings, else an invite accepted here, as is one
     * with a sid; returns that invite's id.
     */
    const forgetUnknownCall = (callSid: string | undefined) => {
      const now = Date.now();
      if (callSid === undefined && pendingPlacedCall(placingCallRef, now)) {
        placingCallRef.current = null;
        return;
      }
      const ringing = ringingInvitesRef.current;
      const inviteId = acceptedInviteId(ringing, now);
      if (inviteId !== undefined) {
        ringing.delete(inviteId);
      }
      return inviteId;
    };

    const register = async () => {
      try {
        // No permission prompts here. Requesting microphone +
        // POST_NOTIFICATIONS on AuthenticatedShell mount (right after
        // login) would hit a brand-new user with sensor-permission
        // dialogs before they did anything needing them. Mic is requested
        // just-in-time inside `makeCall` / `acceptInvite` (and via
        // `getUserMedia` for voice notes); notification permission via the
        // native OS dialog in `useNotificationPermissionPrompt`. Twilio
        // device registration itself only needs the access token + push
        // credentials and works fine without either permission.
        const { token } = await fetchTwilioVoiceToken();
        console.log("[voice] calling login()");
        await CapacitorTwilioVoice.login({ accessToken: token });
        console.log(
          "[voice] login resolved, waiting for registrationSuccess event"
        );
        // Do NOT setRegistered(true) here — login success just means the
        // token is stored. Real push registration is async; the plugin's
        // registrationSuccess listener flips the flag when it completes.
      } catch (err) {
        console.error("[voice] login failed", err);
      }
    };

    const attach = async () => {
      listeners.push(
        await CapacitorTwilioVoice.addListener("registrationSuccess", () => {
          console.log("[voice] registrationSuccess");
          if (_cancelled) {
            return;
          }
          setRegistered(true);
        }),
        await CapacitorTwilioVoice.addListener(
          "registrationFailure",
          ({ error }: { error?: string }) => {
            console.error("[voice] registration failure", error);
            if (_cancelled) {
              return;
            }
            setRegistered(false);
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callInviteReceived",
          ({
            callSid,
            from,
            to,
            customParams,
          }: {
            callSid: string;
            from?: string;
            to?: string;
            customParams?: Record<string, string>;
          }) => {
            if (_cancelled) {
              return;
            }
            // Parse the raw invite fields into a clean caller struct once,
            // here — the UI then branches on `caller.kind` and reads resolved
            // attributes without re-interpreting `from` / custom params.
            const caller = parseCaller({ customParams, from, to });
            // Recorded before the render: a lock-screen answer can deliver
            // `callConnected` before React has rendered this invite.
            ringingInvitesRef.current.set(callSid, {
              caller,
              receivedAt: Date.now(),
            });
            recordInvite({ caller, callSid });
            const h = historyRef.current;
            if (h.location.pathname !== routeTemplates.app.incomingCall) {
              h.push(routeTemplates.app.incomingCall, APP_PUSH_STATE);
            }
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callInviteCancelled",
          ({ callSid }: { callSid: string }) => {
            if (_cancelled) {
              return;
            }
            ringingInvitesRef.current.delete(callSid);
            if (pendingInviteRef.current?.callSid === callSid) {
              recordInvite(null);
            }
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "outgoingCallInitiated",
          ({
            callSid,
            to,
            displayName,
          }: {
            callSid: string;
            to: string;
            displayName?: string;
          }) => {
            if (_cancelled) {
              return;
            }
            placingCallRef.current = null;
            takeScreen({
              callSid,
              direction: "outgoing",
              displayName,
              muted: false,
              onHold: false,
              startedAt: null,
              status: "connecting",
              to,
            });
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callRinging",
          ({ callSid }: { callSid: string }) => {
            if (_cancelled) {
              return;
            }
            const next = ringingCall(callSid, {
              held: activeCallRef.current,
              now: Date.now(),
              placingCallRef,
            });
            if (next) {
              recordCall(next);
            }
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callConnected",
          ({ callSid }: { callSid: string }) => {
            if (_cancelled) {
              return;
            }
            const now = Date.now();
            if (
              updateCall(callSid, (call) =>
                call.status === "connected"
                  ? call
                  : {
                      ...call,
                      startedAt: call.startedAt ?? now,
                      status: "connected",
                    }
              )
            ) {
              return;
            }
            // A call connecting with no call of its own on record is the
            // placed call that rang behind another, or one the host answered.
            const { caller, inviteId, placedCall } = connectingCallParts(
              callSid,
              { now, placingCallRef, ringing: ringingInvitesRef.current }
            );
            takeScreen(
              connectedCall(callSid, {
                caller,
                now,
                placedCall,
              })
            );
            // An Android answer from the notification names no invite, and
            // the notification shows the newest, the one on screen.
            const androidAnswerNamingNoInvite =
              placedCall === null &&
              inviteId === undefined &&
              Capacitor.getPlatform() === "android";
            if (
              pendingInviteRef.current?.callSid === (inviteId ?? callSid) ||
              androidAnswerNamingNoInvite
            ) {
              recordInvite(null);
            }
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callReconnecting",
          ({ callSid }: { callSid: string }) => {
            if (_cancelled) {
              return;
            }
            updateCall(callSid, (call) => ({
              ...call,
              status: "reconnecting",
            }));
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callReconnected",
          ({ callSid }: { callSid: string }) => {
            if (_cancelled) {
              return;
            }
            updateCall(callSid, (call) => ({ ...call, status: "connected" }));
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callHoldChanged",
          ({ callSid, onHold }: { callSid: string; onHold: boolean }) => {
            if (_cancelled) {
              return;
            }
            recordHold(callSid, onHold);
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "callDisconnected",
          ({ callSid }: { callSid?: string }) => {
            if (_cancelled) {
              return;
            }
            const endedId =
              forgetCall(callSid) || Capacitor.getPlatform() !== "android"
                ? callSid
                : forgetUnknownCall(callSid);
            if (pendingInviteRef.current?.callSid === endedId) {
              recordInvite(null);
            }
          }
        ),
        await CapacitorTwilioVoice.addListener(
          "outgoingCallFailed",
          ({ callSid, reason }: { callSid: string; reason?: string }) => {
            if (_cancelled) {
              return;
            }
            placingCallRef.current = null;
            forgetCall(callSid);
            if (reason) {
              console.error("[voice] outgoing call failed", reason);
            }
            toast.error(
              interfaceTranslator("communications.nativeListeners")(
                "errorCallFailed"
              )
            );
          }
        )
      );

      // Recovery for the iOS CallKit lock-screen answer. An incoming call
      // accepted from the native call UI never passes through
      // `acceptInvite`, so the mic is never requested and the call connects
      // with a silent input. iOS only presents the permission dialog while
      // the app is foreground-active, so we ask the moment it becomes
      // active with a live incoming call. Granting won't restore audio for
      // *this* call (the audio session is already running without input),
      // but every subsequent call then has two-way audio. No-op on Android,
      // where the plugin requests the mic at accept time.
      if (Capacitor.getPlatform() === "ios") {
        listeners.push(
          await App.addListener("appStateChange", ({ isActive }) => {
            if (_cancelled || !isActive) {
              return;
            }
            if (activeCallRef.current?.direction !== "incoming") {
              return;
            }
            void ensureMicGranted();
          })
        );
      }
    };

    attach().then(register);

    return () => {
      _cancelled = true;
      setRegistered(false);
      for (const l of listeners) {
        l.remove();
      }
      // Logging out drops every native invite without an event.
      ringingInvitesRef.current.clear();
      recordInvite(null);
      CapacitorTwilioVoice.logout().catch(() => {
        // best-effort logout on unmount; nothing to do if it fails
      });
    };
  }, [supported, activePropertyId]);
}
